import type { BinaryDependencyConfidence } from '../binaryDependencyConfidence';

import type {
  AutoIgnoreReason,
  Category,
  ContainerLayerKind,
  CvssAttackComplexity,
  CvssAttackVector,
  CvssPrivilegesRequired,
  DependencyUsage,
  EpssFilter,
  ExploitMaturity,
  IssueSource,
  LicensingIdentification,
  LicensingIssueType,
  QualityIssueType,
  SeveritySource,
  Ticketed,
  VulnHasFix,
  VulnIgnoreReason,
  VulnIssueType,
  VulnSeverity,
  VulnUpgradeDistance,
} from './common';

export enum IssueFilterKeys {
  Type = 'type',
  Identification = 'identification',
  Depths = 'depths',
  Ticketed = 'ticketed',
  Severity = 'severity',
  Search = 'search',
  ContainerLayers = 'containerLayers',
  HasFix = 'hasFix',
  UpgradeDistance = 'upgradeDistance',
  RevisionIds = 'revisionIds',
  FoundBefore = 'foundBefore',
  FoundAfter = 'foundAfter',
  ExploitMaturity = 'exploitMaturity',
  PackageManagers = 'packageManagers',
  Cwes = 'cwes',
  IgnoreReason = 'ignoreReason',
  ProjectLabels = 'projectLabels',
  Licenses = 'licenses',
  VexStatus = 'vexStatus',
  Epss = 'epss',
  Confidence = 'confidence',
  IssueSource = 'issueSource',
  SeveritySource = 'severitySource',
  CvssAttackVector = 'cvssAttackVector',
  CvssAttackComplexity = 'cvssAttackComplexity',
  CvssPrivilegesRequired = 'cvssPrivilegesRequired',
}

type SearchFilter = {
  [IssueFilterKeys.Search]: string;
};

type SourceFilter = {
  [IssueFilterKeys.Depths]: DependencyUsage[];
};

type ContainerLayersFilter = {
  [IssueFilterKeys.ContainerLayers]: ContainerLayerKind[];
};

type TicketedFilter = {
  [IssueFilterKeys.Ticketed]: Ticketed[];
};

type RevisionIdsFilter = {
  [IssueFilterKeys.RevisionIds]: string[];
};

type FoundBeforeFilter = {
  [IssueFilterKeys.FoundBefore]: string; // ISO 8601 date-time
};

type FoundAfterFilter = {
  [IssueFilterKeys.FoundAfter]: string; // ISO 8601 date-time
};

type PackageManagersFilter = {
  [IssueFilterKeys.PackageManagers]: string[];
};

type ProjectLabelsFilter = {
  [IssueFilterKeys.ProjectLabels]: string[];
};

type ConfidenceFilter = {
  [IssueFilterKeys.Confidence]: BinaryDependencyConfidence[];
};

export type CommonFilter = Partial<
  SearchFilter &
    SourceFilter &
    TicketedFilter &
    RevisionIdsFilter &
    ContainerLayersFilter &
    FoundAfterFilter &
    FoundBeforeFilter &
    PackageManagersFilter &
    ProjectLabelsFilter &
    ConfidenceFilter
>;

export type LicensingFilter = CommonFilter & {
  [IssueFilterKeys.Type]?: LicensingIssueType[];
  [IssueFilterKeys.Identification]?: LicensingIdentification[];
  [IssueFilterKeys.Licenses]?: string[];
  [IssueFilterKeys.IssueSource]?: IssueSource[];
  [IssueFilterKeys.IgnoreReason]?: AutoIgnoreReason[];
};

export type QualityFilter = CommonFilter & {
  [IssueFilterKeys.Type]?: QualityIssueType[];
  [IssueFilterKeys.IssueSource]?: Array<Exclude<IssueSource, IssueSource.Snippet>>;
};

export type VulnFilter = CommonFilter & {
  [IssueFilterKeys.Type]?: VulnIssueType[];
  [IssueFilterKeys.Severity]?: VulnSeverity[];
  [IssueFilterKeys.SeveritySource]?: SeveritySource[];
  [IssueFilterKeys.HasFix]?: VulnHasFix[];
  [IssueFilterKeys.UpgradeDistance]?: VulnUpgradeDistance[];
  [IssueFilterKeys.ExploitMaturity]?: ExploitMaturity[];
  [IssueFilterKeys.IgnoreReason]?: VulnIgnoreReason[];
  [IssueFilterKeys.Cwes]?: string[];
  [IssueFilterKeys.Epss]?: EpssFilter;
  [IssueFilterKeys.IssueSource]?: IssueSource[];
  [IssueFilterKeys.CvssAttackVector]?: CvssAttackVector[];
  [IssueFilterKeys.CvssAttackComplexity]?: CvssAttackComplexity[];
  [IssueFilterKeys.CvssPrivilegesRequired]?: CvssPrivilegesRequired[];
};

export type Filter = LicensingFilter | QualityFilter | VulnFilter;

export type FilterByCategory<C extends Category> = C extends Category.Licensing
  ? LicensingFilter
  : C extends Category.Vulnerability
    ? VulnFilter
    : QualityFilter;
